import { AgenticFlowByline } from '@agentic-flow-studio/shared/components/agentic-flow-byline';
import { BuildInfoBadge } from '@agentic-flow-studio/shared/components/build-info-badge';
import { SidebarNavGroup } from '@agentic-flow-studio/shared/components/layout/SidebarNavGroup';
import { NotificationBell } from '@agentic-flow-studio/shared/components/notifications/NotificationBell';
import { OnboardingChecklist } from '@agentic-flow-studio/shared/components/onboarding/OnboardingChecklist';
import {
  type WorkspaceItem,
  WorkspaceSwitch,
} from '@agentic-flow-studio/shared/components/workspace-switch';
import { purgeWorkspaceLocalState, trpc } from '@agentic-flow-studio/shared/lib/trpc';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
} from '@agentic-flow-studio/shared/ui/sidebar';
import { UserAvatar } from '@agentic-flow-studio/shared/ui/user-avatar';
import { LogOut } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { SkillViperLogo } from '@/components/skill-viper-logo';
import { buildNavItems } from '@/lib/nav-items';
import { onboardingSteps } from '@/lib/onboarding-steps';
import { pushDriver } from '@/lib/push';
import { authClient, clearSkillViperAuthSession } from '@/utils/betterAuth';

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceId, orgId } = useParams<{ workspaceId: string; orgId: string }>();
  const { data: session } = authClient.useSession();

  const meQuery = trpc.auth.me.useQuery(undefined, {
    enabled: !!session,
  });
  const userQuery = trpc.users.me.useQuery(undefined, {
    enabled: !!session,
  });

  const handleLogout = async () => {
    await authClient.signOut();
    clearSkillViperAuthSession();
    purgeWorkspaceLocalState();
    // Hard navigation, not `navigate()`: guarantees no in-memory state (this component's own,
    // or any other module singleton) survives into the next login as a different account.
    window.location.href = '/login';
  };

  const workspaces = meQuery.data?.workspaces || [];
  const activeId = workspaceId ? parseInt(workspaceId, 10) : 0;
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeId);

  const handleWorkspaceSelect = (item: WorkspaceItem) => {
    const ws = workspaces.find((w) => w.id === item.id);
    if (!ws) return;

    const currentPath = location.pathname;
    const pathParts = currentPath.split('/');
    if (
      pathParts.length >= 5 &&
      pathParts[1] === 'org' &&
      pathParts[3] === 'workspace' &&
      pathParts[4] === workspaceId
    ) {
      pathParts[2] = ws.organizationId.toString();
      pathParts[4] = ws.id.toString();
      navigate(pathParts.join('/'));
    } else {
      navigate(`/org/${ws.organizationId}/workspace/${ws.id}/dashboard`);
    }
  };

  // Same definitions the Ctrl/Cmd+K palette renders — see apps/skill-viper-portal/src/lib/nav-items.ts.
  const navItems = useMemo(
    () => buildNavItems(orgId ?? '', workspaceId ?? ''),
    [orgId, workspaceId]
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-border/50">
      <SidebarHeader className="flex flex-col gap-2 p-2 border-b border-border/50 bg-card/10">
        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-2">
          <Link
            to={`/org/${orgId}/workspace/${workspaceId}/dashboard`}
            title="Skill Viper — Old apps. New Tricks"
            className="flex items-center gap-3 min-w-0 rounded-md p-2 transition-colors hover:bg-sidebar-accent/50 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:justify-center"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 border border-primary/20 text-primary shadow-xs">
              <SkillViperLogo className="h-5 w-5 text-primary" />
            </div>
            <span className="text-base font-semibold leading-none tracking-tight truncate group-data-[collapsible=icon]:hidden">
              Skill Viper
            </span>
          </Link>
          <div className="flex shrink-0 items-center gap-1 group-data-[collapsible=icon]:flex-col">
            <NotificationBell
              app="anchor-portal"
              driver={pushDriver}
              orgId={orgId}
              workspaceId={workspaceId}
            />
            <SidebarTrigger className="group-data-[collapsible=icon]:size-8" />
          </div>
        </div>

        <WorkspaceSwitch
          className="px-2 group-data-[collapsible=icon]:hidden"
          workspaces={workspaces}
          activeId={activeId}
          onSelect={handleWorkspaceSelect}
        />
      </SidebarHeader>

      <SidebarContent>
        <OnboardingChecklist
          className="group-data-[collapsible=icon]:hidden"
          steps={onboardingSteps(orgId ?? '', workspaceId ?? '')}
          workspaceLabel={activeWorkspace?.label}
          productKey="anchor_contact"
        />
        <SidebarNavGroup
          label="Workspace"
          items={navItems.filter((item) => item.section === 'Workspace')}
        />
        <SidebarNavGroup
          label="Account"
          className="mt-auto"
          items={navItems.filter((item) => item.section === 'Account')}
          renderIcon={(item) =>
            item.id === 'profile' ? (
              <UserAvatar
                user={
                  userQuery.data ?? {
                    fullName: session?.user?.name,
                    avatarUrl: session?.user?.image,
                    email: session?.user?.email,
                  }
                }
                size="xs"
                showTooltip={false}
                className="h-4 w-4 text-[9px]"
              />
            ) : undefined
          }
        />
      </SidebarContent>

      <SidebarFooter className="p-4 border-t border-border/50 group-data-[collapsible=icon]:p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleLogout}
              className="text-muted-foreground hover:text-destructive transition-colors"
              tooltip="Sign Out"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <BuildInfoBadge appName="Skill Viper Portal" />
        <AgenticFlowByline className="px-2 group-data-[collapsible=icon]:hidden" />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
